import {
  isPrivateSpace,
  isSecretSpace,
} from "@circle-react/helpers/spaceHelpers";
import type { Event } from "@circle-react/types/Event";
import { Alert } from "@circleco/compass/components/Alert";
import { Button } from "@circleco/compass/components/Button";
import { Switch } from "@circleco/compass/components/Switch";
import { Typography } from "@circleco/compass/components/Typography";

import { t } from "@/i18n-js/instance";
import { ShareLinkField } from "@/react/components/PostsCompass/PostModals/ShareModal/ShareLinkField";

import {
  type ShareFormData,
  useShareToWebOptions,
} from "./useShareToWebOptions";

const i18nRoot = "post.share";

interface ShareToWebContentProps {
  getSharePostLink: (args: { postId: number; body: ShareFormData }) => void;
  post: Event;
  sharePostData: { share_post_link: string } | undefined;
}

export const ShareToWebContent = ({
  getSharePostLink,
  post,
  sharePostData,
}: ShareToWebContentProps) => {
  const {
    onClickCopyLink,
    onChange,
    formData,
    currentCommunity,
    currentSpace,
  } = useShareToWebOptions({ post, getSharePostLink, sharePostData });

  const isAllowNonMembersToRsvpActivated =
    post?.event_setting_attributes?.allow_non_members_to_rsvp || false;

  const shouldShowInfoMessage = Boolean(
    isSecretSpace(currentSpace) ||
    isPrivateSpace(currentSpace) ||
    currentSpace?.is_hidden ||
    currentCommunity?.is_private,
  );

  const infoMessage = (() => {
    if (isAllowNonMembersToRsvpActivated) {
      return t([i18nRoot, "share_to_web_public_content_warning"]);
    }
    if (currentCommunity?.is_private && currentSpace?.visibility === "open") {
      return t([i18nRoot, "share_to_web_open_content_warning"]);
    }
    if (
      currentSpace?.is_hidden &&
      !isPrivateSpace(currentSpace) &&
      !isSecretSpace(currentSpace) &&
      !currentCommunity?.is_private
    ) {
      return t([i18nRoot, "share_to_web_hidden_content_warning"]);
    }
    return isSecretSpace(currentSpace)
      ? t([i18nRoot, "share_to_web_secret_content_warning"])
      : t([i18nRoot, "share_to_web_private_content_warning"]);
  })();

  const isPublic =
    !currentCommunity?.is_private &&
    !isPrivateSpace(currentSpace) &&
    !currentSpace?.is_hidden &&
    !isSecretSpace(currentSpace);

  if (isPublic) {
    return (
      <div className="flex items-center gap-4">
        <div className="flex flex-1 flex-col gap-1">
          <Typography variant="heading-sm" color="primary">
            {t([i18nRoot, "share_to_web"])}
          </Typography>
          <Typography variant="body-sm" color="secondary">
            {t([i18nRoot, "post_is_public"])}
          </Typography>
        </div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => onClickCopyLink(null)}
        >
          {t("copy_link")}
        </Button>
      </div>
    );
  }

  return (
    <div className="flex min-w-64 flex-col gap-4">
      <Switch
        label={t([i18nRoot, "share_to_web"])}
        checked={formData.post.share_to_web}
        disabled={isAllowNonMembersToRsvpActivated}
        onCheckedChange={(checked: boolean) => {
          if (isAllowNonMembersToRsvpActivated) return;
          onChange({ share_to_web: checked });
        }}
        labelTooltip={
          isAllowNonMembersToRsvpActivated
            ? t([
                i18nRoot,
                "share_to_web_on_description_allow_non_members_to_rsvp",
              ])
            : t([i18nRoot, "share_to_web_on_description"])
        }
      />
      {formData.post.share_to_web && (
        <>
          <ShareLinkField value={sharePostData?.share_post_link} />
          {(shouldShowInfoMessage || isAllowNonMembersToRsvpActivated) && (
            <Alert
              variant="info"
              icon="circle-info-filled"
              iconColor="info"
              description={infoMessage}
            />
          )}
          {!currentCommunity?.is_private && (
            <Switch
              label={t([i18nRoot, "minimal_layout"])}
              checked={formData.post.minimal_layout}
              onCheckedChange={(checked: boolean) =>
                onChange({ minimal_layout: checked })
              }
              labelTooltip={t([i18nRoot, "minimal_layout_description"])}
            />
          )}
        </>
      )}
    </div>
  );
};
